export enum StorageKey {
  GENERATED_DUOLINGO_TTS = "tts-data.generatedDuolingoTts",
}

/**
 * We simply use a Map object to emulate window.localStorage if the latter
 * isn't available. Nothing will actually be persisted between page reloads,
 * but at least the app will still be usable.
 *
 * localForage is a more aggressive option that we could consider if we really
 * wanted persistence on all browsers, but Art thinks it's overkill for now.
 * https://github.com/localForage/localForage
 *
 * TODO: even if localStorage is available it can still throw exceptions,
 * so we should probably fix code that relies on storage always working
 * and then remove this fallback.
 */
const memoryStorage: Storage = (() => {
  const storage = new Map();
  return {
    clear: () => storage.clear(),
    getItem: key => storage.get(key) ?? null,
    key: index => Array.from(storage.keys())[index],
    get length() {
      return storage.size;
    },
    removeItem: key => storage.delete(key),
    setItem: (key, value) => storage.set(key, value),
  };
})();
const storage = localStorage ?? memoryStorage;

export const getStoredItem = (key: StorageKey) => {
  try {
    const json = storage.getItem(key);
    return json ? JSON.parse(json) : undefined;
  } catch {
    return undefined;
  }
};

export const hasStoredItem = (key: StorageKey) => {
  try {
    return key in storage;
  } catch {
    return false;
  }
};

export const removeStoredItem = (key: StorageKey) => {
  try {
    storage.removeItem(key);
  } catch {
    // localStorage is read-only in Safari private mode :(
  }
};

export const storeItem = (
  key: StorageKey,
  value: Parameters<typeof JSON.stringify>[0],
) => {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage is read-only in Safari private mode :(
  }
};
